import React, { useEffect, useState } from "react";
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import GridOnIcon from "@mui/icons-material/GridOn";
import TableViewIcon from "@mui/icons-material/TableView";
import axios from "axios";
import config from "../../config";
import { getAuthToken } from "../../utils/authStorage";

const methodOptions = [
  { value: "all", label: "All Methods" },
  { value: "qr", label: "QR" },
  { value: "member_id", label: "Member ID" },
  { value: "rfid", label: "RFID" },
];

const MemberAttendanceHistory = () => {
  const [membershipId, setMembershipId] = useState("");
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [attendanceMethod, setAttendanceMethod] = useState("all");
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [loadedQuery, setLoadedQuery] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [exportDialogOpen, setExportDialogOpen] = useState(false);

  const requestConfig = () => {
    const token = getAuthToken();
    return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  };

  const handleExport = async (format) => {
    if (!loadedQuery) return;
    setLoading(true);
    setError("");
    try {
      const response = await axios.post(
        `${config.apiBaseUrl}/export-member-attendance-history`,
        { membership_id: loadedQuery.membershipId, month: loadedQuery.month, attendance_method: loadedQuery.attendanceMethod, format },
        { ...requestConfig(), responseType: "blob" }
      );
      const extension = format === "xlsx" ? "xlsx" : format;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `member_attendance_${loadedQuery.membershipId}_${loadedQuery.month}.${extension}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setExportDialogOpen(false);
    } catch (requestError) {
      setError(requestError.response?.data?.error || `Failed to export ${format.toUpperCase()} history`);
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async (queryOverride = null) => {
    const query = queryOverride || {
      membershipId: membershipId.trim(),
      month,
      attendanceMethod,
    };
    if (!query.membershipId || !query.month) {
      setError("Enter a membership ID and month");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await axios.post(
        `${config.apiBaseUrl}/member-attendance-history`,
        { membership_id: query.membershipId, month: query.month, attendance_method: query.attendanceMethod },
        requestConfig()
      );
      setSummary(response.data.summary || null);
      setRows(response.data.rows || []);
      setLoadedQuery(query);
    } catch (requestError) {
      setSummary(null);
      setRows([]);
      setError(requestError.response?.data?.error || "Failed to load member attendance history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSummary(null);
    setRows([]);
    setLoadedQuery(null);
  }, [attendanceMethod]);

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Member Attendance History</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Review visits and monthly attendance without inventing legacy timestamps.
      </Typography>
      <Box display="grid" gap={2} gridTemplateColumns={{ xs: "1fr", sm: "repeat(4, minmax(0, 1fr))" }} mb={1.5}>
        <TextField label="Membership ID" value={membershipId} onChange={(event) => setMembershipId(event.target.value)} size="small" />
        <TextField label="Month" type="month" value={month} onChange={(event) => setMonth(event.target.value)} InputLabelProps={{ shrink: true }} size="small" />
        <FormControl size="small">
          <InputLabel>Attendance Method</InputLabel>
          <Select value={attendanceMethod} label="Attendance Method" onChange={(event) => setAttendanceMethod(event.target.value)}>
            {methodOptions.map((method) => <MenuItem key={method.value} value={method.value}>{method.label}</MenuItem>)}
          </Select>
        </FormControl>
        <Button variant="contained" startIcon={<SearchIcon />} onClick={() => loadHistory()} disabled={loading} sx={{ textTransform: "none", fontWeight: 700 }}>View History</Button>
      </Box>
      <Box display="flex" gap={1} flexWrap="wrap" mb={2.5}>
        <Button variant="outlined" startIcon={<RefreshOutlinedIcon />} onClick={() => loadHistory(loadedQuery)} disabled={loading || !loadedQuery} sx={{ textTransform: "none", fontWeight: 700 }}>
          Refresh History
        </Button>
        <Button
          variant="text"
          startIcon={<ClearOutlinedIcon />}
          onClick={() => { setMembershipId(""); setSummary(null); setRows([]); setLoadedQuery(null); setError(""); }}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          Clear
        </Button>
        <Button variant="contained" startIcon={<DownloadIcon />} onClick={() => setExportDialogOpen(true)} disabled={loading || !loadedQuery} sx={{ textTransform: "none", fontWeight: 700 }}>
          Generate Report
        </Button>
      </Box>
      {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
      {summary && (
        <Box display="grid" gap={1.5} gridTemplateColumns={{ xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" }} mb={2}>
          {[
            ["Total Visits", summary.total_visits, "#246b8f"],
            ["Last Visit", summary.last_visit, "#4cceac"],
            ["Monthly Attendance", summary.monthly_attendance, "#f39c12"],
            ["Attendance Percentage", summary.attendance_percentage ?? "N/A", "#8e7dff"],
          ].map(([label, value, accent]) => (
            <Box key={label} sx={{ p: 1.75, borderRadius: 2, borderTop: `3px solid ${accent}`, backgroundColor: "action.hover" }}>
              <Typography variant="caption" color="text.secondary">{label}</Typography>
              <Typography variant="h6" fontWeight={800}>{value}</Typography>
            </Box>
          ))}
        </Box>
      )}
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Generate Member Attendance History</DialogTitle>
        <DialogContent>
          <Box display="grid" gap={1.25}>
            <Button variant="contained" startIcon={<PictureAsPdfIcon />} onClick={() => handleExport("pdf")} sx={{ justifyContent: "flex-start", backgroundColor: "#c0392b", color: "#fff", "&:hover": { backgroundColor: "#a93226" } }}>PDF Document</Button>
            <Button variant="contained" startIcon={<GridOnIcon />} onClick={() => handleExport("xlsx")} sx={{ justifyContent: "flex-start", backgroundColor: "#217346", color: "#fff", "&:hover": { backgroundColor: "#185c37" } }}>Excel Workbook</Button>
            <Button variant="contained" startIcon={<TableViewIcon />} onClick={() => handleExport("csv")} sx={{ justifyContent: "flex-start", backgroundColor: "#246b8f", color: "#fff", "&:hover": { backgroundColor: "#1b526e" } }}>CSV File</Button>
          </Box>
        </DialogContent>
        <DialogActions><Button onClick={() => setExportDialogOpen(false)}>Cancel</Button></DialogActions>
      </Dialog>
    </Box>
  );
};

export default MemberAttendanceHistory;
